import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Checkbox,
  Box as DarkBox,
  DarkProvider,
  SearchIcon,
  TextInput,
} from '@hivewatch/dark'

import { DragEventI, Draggable } from '@/components/ui'

import { DarkFloorPlanSidePanel } from '../../dark/DarkFloorPlanSidePanel'
import { DeviceI } from '../../types/types'
import { DarkDeviceListItem } from './DarkDeviceListItem'
import { darkDevicesPanelStyles } from './DarkDevicesPanel.styles'

const SKELETON_ROW_COUNT = 8

export interface DarkDevicesPanelIProps {
  devices: DeviceI[]
  isExpanded: boolean
  onToggleExpansion: () => void
  onTileDragStart: (event: DragEventI) => void
  onDeviceClicked: (device: DeviceI) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onDeviceItemHoverOut: () => void
}

export const DarkDevicesPanel = ({
  devices,
  isExpanded,
  onToggleExpansion,
  onTileDragStart,
  onDeviceClicked,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: DarkDevicesPanelIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  // Keep as old Edit Mode check! Dragging devices onto the map must stay an
  // explicit action.
  const isEditMode = useIsEditMode()
  const {
    nameLikeFilter,
    setNameLikeFilter,
    isFloorDataLoading,
    shouldShowAssociatedDevices,
    setShouldShowAssociatedDevices,
  } = useFloorPlan()

  const s = darkDevicesPanelStyles(colorScheme)

  const header = (
    <DarkBox {...s.header}>
      <DarkBox
        as='h2'
        data-testid='floorPlanPage_devicePanel_devices'
        {...s.headerTitle}
      >
        {t('floors:devices_panel_title')}
      </DarkBox>
      <TextInput
        colorScheme={colorScheme}
        data-testid='floorPlanPage_darkDevicesPanel_search'
        leftIcon={<SearchIcon colorScheme={colorScheme} size={14} />}
        onValueChange={setNameLikeFilter}
        placeholder={t('floors:devices_panel_search_placeholder')}
        value={nameLikeFilter}
        width='100%'
      />
      <Checkbox
        checked={shouldShowAssociatedDevices}
        colorScheme={colorScheme}
        data-testid='floorPlanPage_darkDevicesPanel_associatedToggle'
        label={
          <DarkBox as='span' {...s.checkboxLabel}>
            {t('floors:devices_panel_show_associated')}
          </DarkBox>
        }
        onChange={(e) => setShouldShowAssociatedDevices(e.target.checked)}
      />
    </DarkBox>
  )

  return (
    <DarkProvider>
      <DarkFloorPlanSidePanel
        dataTestid='floorPlanPage_darkDevicesPanel'
        header={header}
        isExpanded={isExpanded}
        onToggleExpansion={onToggleExpansion}
      >
        {isFloorDataLoading ? (
          <DarkBox {...s.list}>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
              <DarkBox key={`device-skeleton-${i}`} {...s.skeletonRow}>
                <DarkBox {...s.skeletonIcon} />
                <DarkBox {...s.skeletonLine} />
              </DarkBox>
            ))}
          </DarkBox>
        ) : devices.length === 0 ? (
          <DarkBox {...s.emptyState}>
            {t('floors:devices_panel_no_devices')}
          </DarkBox>
        ) : (
          <DarkBox {...s.list}>
            {devices.map((d) => (
              <Draggable
                dragData={d}
                dragEnabled={isEditMode}
                key={d.id}
                onDragStart={onTileDragStart}
              >
                <DarkBox onClick={() => onDeviceClicked(d)}>
                  <DarkDeviceListItem
                    colorScheme={colorScheme}
                    deviceHealth={d.deviceHealth}
                    deviceId={d.id}
                    deviceName={d.deviceName || d.id}
                    deviceType={d.deviceType}
                    onDeviceItemHoverIn={onDeviceItemHoverIn}
                    onDeviceItemHoverOut={onDeviceItemHoverOut}
                    placed={d.placed}
                    selected={d.isEditMode}
                  />
                </DarkBox>
              </Draggable>
            ))}
          </DarkBox>
        )}
      </DarkFloorPlanSidePanel>
    </DarkProvider>
  )
}
